/** Maps accident-atlas chart config to Chart.js options: https://www.chartjs.org/ */
import { ChartData, ChartOptions, FontSpec } from 'chart.js'

import {
  AccidentAtlasCartesianChartConfig,
  AccidentAtlasChartConfig,
  AccidentAtlasPieChartConfig,
  isCartesianChartConfig,
  isPieChartConfig,
} from '../types/api'
import { AccidentAtlasChartRenderType } from '../types/tile'
import {
  getAlternatingChartColor,
  getChartColor,
  getSeriesLineDash,
  getSeriesPointStyle,
} from './chartColors'

const CHART_TEXT_COLOR = '#262626'
const CHART_TOOLTIP_TEXT_COLOR = '#FFFFFF'
const CHART_GRID_COLOR = '#DCD9D8'
const CHART_SLICE_BORDER_COLOR = '#FFFFFF'

const CHART_AXIS_FONT: Partial<FontSpec> = {
  family: '"DIN Next LT Pro", DINPro, sans-serif',
  size: 12,
  weight: 400,
}

const CHART_LEGEND_FONT: Partial<FontSpec> = {
  family: 'DINPro, "DIN Pro", sans-serif',
  size: 12,
  weight: 500,
}

const LEGEND_LABELS = {
  boxWidth: 8,
  boxHeight: 8,
  padding: 20,
  usePointStyle: true,
  pointStyle: 'circle' as const,
}

export type PieChartJsConfig = {
  data: ChartData<'pie', number[], string>
  options: ChartOptions<'pie'>
}

export type BarChartJsConfig = {
  data: ChartData<'bar', number[], string>
  options: ChartOptions<'bar'>
}

export type LineChartJsConfig = {
  data: ChartData<'line', number[], string>
  options: ChartOptions<'line'>
}

export type ChartJsBuildOptions = {
  yMax?: number
  colorOffset?: number
  reducedMotion?: boolean
}

export type CartesianChartBuildOptions = ChartJsBuildOptions

function buildAnimationOptions(reducedMotion = false) {
  if (reducedMotion) {
    return { duration: 0 }
  }

  return {
    duration: 500,
    easing: 'easeOutQuart' as const,
  }
}

function buildCartesianOptions<TType extends 'bar' | 'line'>(
  chart: AccidentAtlasCartesianChartConfig,
  showLegend: boolean,
  options: ChartJsBuildOptions = {}
): ChartOptions<TType> {
  const { yMax, reducedMotion = false } = options

  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: buildAnimationOptions(reducedMotion),
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: showLegend,
        position: 'bottom',
        align: 'center',
        labels: {
          ...LEGEND_LABELS,
          color: CHART_TEXT_COLOR,
          font: CHART_LEGEND_FONT,
        },
      },
      tooltip: {
        enabled: true,
        titleFont: CHART_AXIS_FONT,
        bodyFont: CHART_AXIS_FONT,
        footerFont: CHART_AXIS_FONT,
        titleColor: CHART_TOOLTIP_TEXT_COLOR,
        bodyColor: CHART_TOOLTIP_TEXT_COLOR,
        footerColor: CHART_TOOLTIP_TEXT_COLOR,
      },
    },
    scales: {
      x: {
        title: {
          display: Boolean(chart.xAxis.name),
          text: chart.xAxis.name,
          color: CHART_TEXT_COLOR,
          font: CHART_AXIS_FONT,
        },
        ticks: {
          color: CHART_TEXT_COLOR,
          font: CHART_AXIS_FONT,
          maxRotation: 45,
          minRotation: 0,
        },
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        ...(yMax !== undefined ? { max: yMax } : {}),
        title: {
          display: Boolean(chart.yAxis.name),
          text: chart.yAxis.name,
          color: CHART_TEXT_COLOR,
          font: CHART_AXIS_FONT,
        },
        ticks: {
          color: CHART_TEXT_COLOR,
          font: CHART_AXIS_FONT,
        },
        grid: {
          color: CHART_GRID_COLOR,
        },
        border: {
          display: false,
        },
      },
    },
  } as unknown as ChartOptions<TType>
}

export function chartToPieChartJsConfig(
  chart: AccidentAtlasPieChartConfig,
  options: ChartJsBuildOptions = {}
): PieChartJsConfig {
  const { reducedMotion = false } = options
  const values = chart.datasets[0]?.data ?? []
  const colors = chart.labels.map((_, index) => getChartColor(index))

  return {
    data: {
      labels: chart.labels,
      datasets: [
        {
          data: values,
          backgroundColor: colors,
          borderColor: CHART_SLICE_BORDER_COLOR,
          borderWidth: 2,
          hoverOffset: reducedMotion ? 0 : 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: buildAnimationOptions(reducedMotion),
      cutout: '45%',
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          titleFont: CHART_AXIS_FONT,
          bodyFont: CHART_AXIS_FONT,
          titleColor: CHART_TOOLTIP_TEXT_COLOR,
          bodyColor: CHART_TOOLTIP_TEXT_COLOR,
        },
      },
    },
  }
}

function buildCartesianDatasets(
  chart: AccidentAtlasCartesianChartConfig,
  type: 'ColumnChart' | 'LineChart',
  colorOffset = 0
) {
  const isLine = type === 'LineChart'
  const isBar = type === 'ColumnChart'

  return chart.series.map((series, index) => {
    const color = getChartColor(index)
    const barColors = isBar
      ? series.data.map((_, dataIndex) =>
          getAlternatingChartColor(colorOffset + dataIndex)
        )
      : undefined

    return {
      label: series.name,
      data: series.data,
      backgroundColor: barColors ?? color,
      borderColor: barColors ?? color,
      borderWidth: isLine ? 2 : 0,
      borderDash: isLine ? getSeriesLineDash(index) : undefined,
      borderRadius: isBar ? 2 : 0,
      minBarLength: isBar ? 2 : undefined,
      maxBarThickness: isBar ? 40 : undefined,
      pointRadius: isLine ? 3 : 0,
      pointHoverRadius: isLine ? 4 : 0,
      pointBackgroundColor: color,
      pointStyle: isLine ? getSeriesPointStyle(index) : undefined,
      tension: 0.2,
    }
  })
}

export function chartToBarChartJsConfig(
  chart: AccidentAtlasCartesianChartConfig,
  options: CartesianChartBuildOptions = {}
): BarChartJsConfig {
  const showLegend = chart.series.length > 1
  const { yMax, colorOffset = 0, reducedMotion = false } = options

  return {
    data: {
      labels: chart.xAxis.categories,
      datasets: buildCartesianDatasets(chart, 'ColumnChart', colorOffset),
    },
    options: buildCartesianOptions<'bar'>(chart, showLegend, {
      yMax,
      reducedMotion,
    }),
  }
}

export function chartToLineChartJsConfig(
  chart: AccidentAtlasCartesianChartConfig,
  options: ChartJsBuildOptions = {}
): LineChartJsConfig {
  const showLegend = chart.series.length > 1
  const { reducedMotion = false } = options

  return {
    data: {
      labels: chart.xAxis.categories,
      datasets: buildCartesianDatasets(chart, 'LineChart'),
    },
    options: buildCartesianOptions<'line'>(chart, showLegend, {
      reducedMotion,
    }),
  }
}

export function chartToChartJsConfig(
  chart: AccidentAtlasChartConfig,
  type: AccidentAtlasChartRenderType,
  options: ChartJsBuildOptions = {}
): PieChartJsConfig | BarChartJsConfig | LineChartJsConfig {
  if (type === 'PieChart') {
    if (!isPieChartConfig(chart)) {
      throw new Error('PieChart tile requires pie chartConfig')
    }
    return chartToPieChartJsConfig(chart, options)
  }

  if (!isCartesianChartConfig(chart)) {
    throw new Error('Line/column tile requires cartesian chartConfig')
  }

  if (type === 'LineChart') {
    return chartToLineChartJsConfig(chart, options)
  }

  return chartToBarChartJsConfig(chart, options)
}
